import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Languages, ArrowLeftRight, Loader2, Volume2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function DarijaTranslator() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [direction, setDirection] = useState("en-darija");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const translate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/translate", { text, direction });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const speak = (value) => {
    if (!value || !window.speechSynthesis) return;
    const u = new SpeechSynthesisUtterance(value);
    u.lang = "ar";
    window.speechSynthesis.speak(u);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="darija-fab"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-secondary px-5 py-3.5 font-medium text-secondary-foreground shadow-[0_8px_32px_rgba(37,99,235,0.35)] transition-transform duration-200 hover:-translate-y-1"
        >
          <Languages className="h-5 w-5" />
          <span className="hidden sm:inline">Darija Translator</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full max-w-md overflow-y-auto sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2 text-2xl">
            <Languages className="h-6 w-6 text-secondary" /> Live Darija Translator
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="flex items-center justify-center gap-3 rounded-full bg-muted p-1.5 text-sm font-medium">
            <span className={direction === "en-darija" ? "rounded-full bg-card px-3 py-1.5 shadow" : "px-3 py-1.5"}>
              English
            </span>
            <button
              data-testid="translator-swap"
              onClick={() => { setDirection((d) => (d === "en-darija" ? "darija-en" : "en-darija")); setResult(null); }}
              className="rounded-full p-1.5 text-secondary transition-transform hover:rotate-180"
            >
              <ArrowLeftRight className="h-4 w-4" />
            </button>
            <span className={direction === "darija-en" ? "rounded-full bg-card px-3 py-1.5 shadow" : "px-3 py-1.5"}>
              Darija
            </span>
          </div>

          <Textarea
            data-testid="translator-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={direction === "en-darija" ? "e.g. How much does this cost?" : "e.g. Bch-hal hada?"}
            className="min-h-[100px] resize-none rounded-xl"
          />

          <Button
            data-testid="translator-submit"
            onClick={translate}
            disabled={loading || !text.trim()}
            className="w-full rounded-full"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
            Translate
          </Button>

          {result && (
            <div className="space-y-3 rounded-2xl border bg-card p-5" data-testid="translator-result">
              <div className="flex items-start justify-between gap-3">
                <p className="font-display text-2xl leading-snug">{result.translation}</p>
                <button onClick={() => speak(result.translation)} className="mt-1 text-secondary" aria-label="Speak">
                  <Volume2 className="h-5 w-5" />
                </button>
              </div>
              {result.latin_transliteration && (
                <p className="text-sm italic text-muted-foreground">/ {result.latin_transliteration} /</p>
              )}
              {result.pronunciation_tip && (
                <div>
                  <p className="overline text-secondary">Pronunciation</p>
                  <p className="text-sm">{result.pronunciation_tip}</p>
                </div>
              )}
              {result.context_note && (
                <div>
                  <p className="overline text-primary">Local tip</p>
                  <p className="text-sm">{result.context_note}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
